import type { Fragment, Node } from '@atlaskit/editor-prosemirror/model';
import { nodeToReact } from '@atlaskit/renderer/nodes';
import type { ReactSerializerInit } from '@atlaskit/renderer/react';

import { IncrementalHeightEstimator } from '../../utils/node-estimated-height.ts';

import { lightSupportedNodeNames, lightTableNodeNames } from './LightADFNodeComponents.tsx';
import { getTableBatchEnds } from './tableBatchEnds.ts';
import { getColumnRows, holdsNestedContent } from './nodes/table/rowForms.ts';
import { getColumnWidths } from './nodes/table/rowHeightEstimate.ts';
import { getTextRowData } from './nodes/table/TextRow.tsx';
import type { TextRowData } from './nodes/table/TextRow.tsx';

const supportedWithTables = new Set<string>(lightSupportedNodeNames);
const supportedWithoutTables = new Set<string>(
	lightSupportedNodeNames.filter((name) => !lightTableNodeNames.includes(name)),
);

export type LightFallbackReason =
	| 'first-batch'
	| 'unsupported-node'
	| 'custom-node'
	| 'configuration';

// Platform merges its lazy defaults into nodeComponents; only actual overrides need fallback.
const hasCustomComponent = (node: Node, init: ReactSerializerInit) =>
	Boolean(
		init.nodeComponents &&
		Object.prototype.hasOwnProperty.call(init.nodeComponents, node.type.name) &&
		init.nodeComponents[node.type.name] !== nodeToReact[node.type.name],
	);

function nodeFallback(
	node: Node,
	init: ReactSerializerInit,
	supported: ReadonlySet<string>,
): LightFallbackReason | undefined {
	// Every eligible card must be able to upgrade to Platform's URL-backed component.
	if (node.type.name === 'inlineCard' && !node.attrs.url) {
		return 'unsupported-node';
	}
	// Missing names need resolution; the unknown-user marker needs Platform's localized fallback.
	if (node.type.name === 'mention' && (!node.attrs.text || node.attrs.text === '@_|unknown|_')) {
		return 'unsupported-node';
	}
	if (!node.isText && !supported.has(node.type.name)) {
		return 'unsupported-node';
	}
	if (hasCustomComponent(node, init)) {
		return 'custom-node';
	}
	if (
		// These options need full-renderer behavior or document-wide mark bookkeeping.
		init.contentMode === 'compact' ||
		node.marks.some(
			(mark) => mark.type.name === 'backgroundColor' || mark.type.name === 'annotation',
		)
	) {
		return 'configuration';
	}
	return undefined;
}

const hasSupportedInlineChild = (node: Node, supported: ReadonlySet<string>): boolean =>
	node.content.content.some((child) => !child.isText && supported.has(child.type.name));

export type LightBatchPlan = {
	batchByPosition: Map<number, number>;
	batchEndPositions: Set<number>;
	/** Fallbacks keyed by top-level subtree position, independently of height batches. */
	fallbackByPosition: Map<number, LightFallbackReason>;
	/** ADF nodes contained in top-level subtrees initially rendered in light mode. */
	initialLightNodeCount: number;
	lightNodeCountByPosition: Map<number, number>;
	/** Descendant-inclusive counts attributed to each full subtree's selected fallback. */
	fullNodeCountsByReason: Record<LightFallbackReason, number>;
	/** All ADF nodes considered when planning light subtrees. */
	totalNodeCount: number;
	/** Rows of full top-level tables after which a height batch ends, keyed by table. */
	tableBatchEndRows: Map<Node, ReadonlySet<Node>>;
	/** Table rows merged into text rows, without serializing their content, in light subtrees. */
	textRows: Map<Node, TextRowData>;
	/** Table rows that keep their columns and render their content with the real components. */
	columnRows: Set<Node>;
};

export type LightBatchPlanOptions = {
	allowVirtualizeTables?: boolean;
};

/** Plan height batches and preflight each complete top-level subtree. No React trees are built. */
export function planLightBatches(
	fragment: Fragment,
	init: ReactSerializerInit,
	batchHeight: number,
	{ allowVirtualizeTables = false }: LightBatchPlanOptions = {},
): LightBatchPlan {
	const batchByPosition = new Map<number, number>();
	const batchEndPositions = new Set<number>();
	const fallbackByPosition = new Map<number, LightFallbackReason>();
	const tableBatchEndRows = new Map<Node, ReadonlySet<Node>>();
	const textRows = new Map<Node, TextRowData>();
	const supported = allowVirtualizeTables ? supportedWithTables : supportedWithoutTables;
	const estimator = new IncrementalHeightEstimator({ estimateFloatingMedia: true });
	let batch = 0;
	let lastBatchHeight = 0;
	let height = 0;
	let subtreeFallsBack = false;
	let initialLightNodeCount = 0;
	const lightNodeCountByPosition = new Map<number, number>();
	let totalNodeCount = 0;
	let subtreeNodeCount = 0;
	const fullNodeCountsByReason: Record<LightFallbackReason, number> = {
		'first-batch': 0,
		'unsupported-node': 0,
		'custom-node': 0,
		configuration: 0,
	};

	const columnRows = new Set<Node>();
	// Row content renders as text or with the real components, so it only needs these checks.
	const rowContentFallback = (node: Node): LightFallbackReason | undefined => {
		if (hasCustomComponent(node, init)) {
			return 'custom-node';
		}
		if (holdsNestedContent(node)) {
			return 'unsupported-node';
		}
		// Background colour marks need the full renderer's document-wide mark bookkeeping.
		if (node.marks.some((mark) => mark.type.name === 'backgroundColor')) {
			return 'configuration';
		}
		return undefined;
	};

	const visit = (node: Node, path: Node[], insideRow: boolean): LightFallbackReason | undefined => {
		totalNodeCount++;
		subtreeNodeCount++;
		let reason: LightFallbackReason | undefined;
		// The first batch always renders full; only height estimation is needed for its nodes.
		if (batch !== 0) {
			reason = insideRow ? rowContentFallback(node) : nodeFallback(node, init, supported);
		}
		subtreeFallsBack ||= Boolean(reason);
		if (
			allowVirtualizeTables &&
			!insideRow &&
			!subtreeFallsBack &&
			batch !== 0 &&
			node.type.name === 'table'
		) {
			const tableColumnRows = getColumnRows(node);
			const columnWidths = getColumnWidths(node);
			node.forEach((row) => {
				if (tableColumnRows.has(row)) {
					columnRows.add(row);
				} else {
					textRows.set(row, getTextRowData(row, init.allowPlaceholderText, columnWidths));
				}
			});
		}
		const childrenInsideRow = insideRow || (allowVirtualizeTables && node.type.name === 'tableRow');
		estimator.enterNode();
		const childPath = [...path, node];
		node.forEach((child) => {
			// Always visit descendants for height estimation, even after finding a fallback.
			const childReason = visit(child, childPath, childrenInsideRow);
			reason = reason ?? childReason;
		});
		height = estimator.leaveNode(childPath);
		return reason;
	};

	fragment.forEach((node, offset) => {
		const position = (init.startPos || 1) + offset;
		batchByPosition.set(position, batch);
		subtreeFallsBack = false;
		subtreeNodeCount = 0;
		const startHeight = height;
		const reason = visit(node, [], false);
		if (batch === 0) {
			fallbackByPosition.set(position, 'first-batch');
		} else if (reason) {
			fallbackByPosition.set(position, reason);
		} else if (node.type.name === 'paragraph' && !hasSupportedInlineChild(node, supported)) {
			// Only standalone paragraphs need an eligible inline child to justify an upgrade.
			// Paragraphs inside panels and other supported subtrees keep their existing eligibility.
			fallbackByPosition.set(position, 'unsupported-node');
		}
		const fallbackReason = fallbackByPosition.get(position);
		if (fallbackReason) {
			fullNodeCountsByReason[fallbackReason] += subtreeNodeCount;
		} else {
			initialLightNodeCount += subtreeNodeCount;
			lightNodeCountByPosition.set(position, subtreeNodeCount);
		}
		// Only a table that renders full can span batches; a light subtree upgrades as one block.
		if (node.type.name === 'table' && fallbackByPosition.has(position)) {
			const tableBatchEnds = getTableBatchEnds(node, startHeight, lastBatchHeight, batchHeight);
			if (tableBatchEnds.rows.size) {
				tableBatchEndRows.set(node, tableBatchEnds.rows);
				batch += tableBatchEnds.rows.size;
				lastBatchHeight = tableBatchEnds.lastBatchHeight;
			}
		}
		// Progressive visibility still uses height batches, regardless of subtree eligibility.
		if (height - lastBatchHeight >= batchHeight) {
			batchEndPositions.add(position);
			batch++;
			lastBatchHeight = height;
		}
	});
	return {
		batchByPosition,
		batchEndPositions,
		fallbackByPosition,
		initialLightNodeCount,
		lightNodeCountByPosition,
		fullNodeCountsByReason,
		totalNodeCount,
		tableBatchEndRows,
		textRows,
		columnRows,
	};
}
